"use client";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import type { Key } from "react";
import { Parallax } from "react-scroll-parallax";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import CustomImage from "@/components/shared/CustomImage";
import Title from "@/components/shared/Title";
import { useLanguage } from "@/contexts/LanguageContext";
import type {
  About as AboutType,
  Home as HomeType,
  Media,
} from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";
import type { PayLoadData } from "@/utils/payloadUtil";

interface AboutProps {
  pageData?: PayLoadData<AboutType | undefined> | undefined;
  homeData?: PayLoadData<HomeType | undefined> | undefined;
}

const About = ({ pageData, homeData }: AboutProps) => {
  const { language } = useLanguage();

  let aboutData: any = null;

  if (pageData) {
    aboutData = pageData[language]?.aboutUs;
  } else if (homeData) {
    aboutData = homeData[language]?.aboutUsHome || "";
  }

  if (!aboutData) {
    return null;
  }

  return (
    <section id="about-story" className="bg-primary-50">
      <div className="container mx-auto">
        {/* About Company Section */}
        <Title
          title={aboutData?.titleOurAbout || ""}
          desc={aboutData?.descOurAbout || ""}
        />

        {/* Slide with thumbnail navigation */}
        {(() => {
          return (
            <div className="overflow-hidden md:aspect-video">
              <div className="flex size-full items-center justify-center shadow">
                <Swiper
                  effect="slide"
                  slidesPerView={1}
                  speed={900}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 3000,
                  }}
                  modules={[Navigation, Pagination, Autoplay]}
                  loop
                  className="custom-navigation custom-pagination pagination-no-absolute md:light dark size-full"
                >
                  {aboutData?.slideOurAbout?.map(
                    (item: any, idx: Key | null | undefined) => (
                      <SwiperSlide key={idx} className="overflow-hidden">
                        {/* desktop */}
                        <div className="hidden md:block">
                          <Parallax
                            scale={[1, 2]}
                            className="absolute inset-0 size-full"
                          >
                            <CustomImage
                              src={getImageUrl(item.image)}
                              alt={
                                (item.image as Media)?.alt || "image-our-about"
                              }
                              fill
                              className="size-full object-cover object-center"
                            />
                          </Parallax>
                          <Parallax
                            translateY={["200px", "0px"]}
                            className="absolute left-0 top-0 w-full max-w-full bg-primary-500/85 px-4 py-12 text-center md:max-w-xl md:px-12 md:py-16 md:text-left"
                          >
                            <h2
                              className="mb-4 text-3xl font-bold text-white md:text-4xl"
                              style={{ fontFamily: "inherit" }}
                            >
                              {item?.title || ""}
                            </h2>
                            <p className="mb-6 text-lg text-white">
                              {item?.desc || ""}
                            </p>
                          </Parallax>
                        </div>

                        {/* mobile */}
                        <div className="flex h-full flex-col md:hidden">
                          <div className="relative inset-0 aspect-square">
                            <CustomImage
                              src={getImageUrl(item.image)}
                              alt={
                                (item.image as Media)?.alt || "image-our-about"
                              }
                              fill
                              className="size-full object-cover object-center"
                            />
                          </div>
                          <div className="flex-1 bg-primary-500 px-4 py-8">
                            <div className="flex h-full min-h-52 flex-col justify-center space-y-4">
                              <h2
                                className="text-3xl font-bold text-white"
                                style={{ fontFamily: "inherit" }}
                              >
                                {item?.title || ""}
                              </h2>
                              <p className="text-lg text-white">
                                {item?.desc || ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    )
                  )}
                </Swiper>
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
};

export default About;
